import { useState, useEffect } from "react"
import { Plus, Plug2, RefreshCw, Trash2, Shield, Eye, EyeOff, CheckCircle, XCircle, AlertCircle, Lock } from "lucide-react"
import Modal from "../components/common/Modal"
import Badge from "../components/common/Badge"
import api from "../services/api"
import { EXCHANGES } from "../utils/constants"
import { formatRelative, formatCurrency } from "../utils/formatters"
import toast from "react-hot-toast"

const EX_META = {
  binance:   { bg: "#F0B90B", text: "#000", abbr: "BN" },
  binanceus: { bg: "#F0B90B", text: "#000", abbr: "BUS" },
  coinbase:  { bg: "#0052FF", text: "#fff", abbr: "CB" },
  kraken:    { bg: "#5741D9", text: "#fff", abbr: "KR" },
  bybit:     { bg: "#FF6B00", text: "#fff", abbr: "BY" },
  okx:       { bg: "#333",    text: "#fff", abbr: "OKX" },
  kucoin:    { bg: "#00A478", text: "#fff", abbr: "KC" },
  gate:      { bg: "#2354E6", text: "#fff", abbr: "GT" },
  huobi:     { bg: "#1F64F1", text: "#fff", abbr: "HT" },
  bitfinex:  { bg: "#16B157", text: "#fff", abbr: "BFX" },
}

const FEATURED = [
  { id: "binance",  name: "Binance",   tag: "Most Popular" },
  { id: "bybit",    name: "Bybit",     tag: "Futures" },
  { id: "okx",      name: "OKX",       tag: "Web3" },
  { id: "coinbase", name: "Coinbase",  tag: "US Regulated" },
  { id: "kucoin",   name: "KuCoin",    tag: "Altcoins" },
  { id: "kraken",   name: "Kraken",    tag: "Secure" },
]

function ExLogo({ id, size = 40 }) {
  const m = EX_META[id] || { bg: "#374151", text: "#fff", abbr: "?" }
  return (
    <div style={{ width: size, height: size, background: m.bg, color: m.text, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: size * 0.28, letterSpacing: -0.5, flexShrink: 0 }}>
      {m.abbr}
    </div>
  )
}

function ConnectModal({ isOpen, onClose, onCreated }) {
  const [form, setForm] = useState({ exchange_id: "binance", label: "", api_key: "", api_secret: "", passphrase: "", is_testnet: true })
  const [showKey, setShowKey] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    try {
      await api.post("/exchanges/", form)
      toast.success("Exchange connected!")
      onCreated()
      onClose()
      setForm({ exchange_id: "binance", label: "", api_key: "", api_secret: "", passphrase: "", is_testnet: true })
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection failed")
    } finally { setSubmitting(false) }
  }

  const sel = EXCHANGES.find(e => e.id === form.exchange_id)
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Connect Exchange">
      <div className="flex items-center gap-3 p-3 bg-surface-100 rounded-xl mb-5">
        <ExLogo id={form.exchange_id} size={36} />
        <div>
          <p className="text-sm font-semibold text-white">{sel?.name}</p>
          <p className="text-xs text-gray-400">API Key Connection</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Exchange</label>
          <select className="input" value={form.exchange_id} onChange={e => setForm({ ...form, exchange_id: e.target.value })}>
            {EXCHANGES.map(ex => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Label</label>
          <input className="input" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} required placeholder="e.g. My Binance Main" />
        </div>
        <div>
          <label className="label">API Key</label>
          <input className="input font-mono text-xs" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })} required placeholder="Paste your API key" autoComplete="off" />
        </div>
        <div className="relative">
          <label className="label">API Secret</label>
          <input type={showKey ? "text" : "password"} className="input font-mono text-xs pr-10" value={form.api_secret} onChange={e => setForm({ ...form, api_secret: e.target.value })} required autoComplete="off" />
          <button type="button" onClick={() => setShowKey(s => !s)} className="absolute right-3 bottom-2.5 text-gray-400 hover:text-white">
            {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {["kucoin","okx","coinbase"].includes(form.exchange_id) && (
          <div>
            <label className="label">Passphrase</label>
            <input type="password" className="input font-mono text-xs" value={form.passphrase} onChange={e => setForm({ ...form, passphrase: e.target.value })} />
          </div>
        )}
        <label className="flex items-center gap-3 cursor-pointer p-3 bg-surface-100 rounded-xl">
          <input type="checkbox" checked={form.is_testnet} onChange={e => setForm({ ...form, is_testnet: e.target.checked })} className="w-4 h-4 accent-indigo-500 rounded" />
          <div>
            <p className="text-sm text-white font-medium">Use Testnet / Sandbox</p>
            <p className="text-xs text-gray-400">Paper trade without real funds — recommended to start</p>
          </div>
        </label>
        <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl space-y-1 text-xs text-emerald-400">
          <div className="flex items-center gap-2 font-semibold mb-1"><Shield size={13} /> Security Guarantee</div>
          <p>• Encrypted with AES-256 (Fernet) before storage</p>
          <p>• Withdrawal permissions are always disabled</p>
          <p>• Only Read + Trade permissions are used</p>
        </div>
        <button type="submit" disabled={submitting} className="btn-primary w-full">
          {submitting ? "Verifying connection…" : "Connect Exchange"}
        </button>
      </form>
    </Modal>
  )
}

function ConnectionCard({ conn, onVerify, onDelete, verifying }) {
  const exchange = EXCHANGES.find(ex => ex.id === conn.exchange_id)
  const meta = EX_META[conn.exchange_id]
  const isOk = conn.status === "active"
  return (
    <div className="card hover:border-surface-300 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <ExLogo id={conn.exchange_id} size={44} />
          <div>
            <h3 className="font-semibold text-white">{conn.label}</h3>
            <p className="text-xs text-gray-400">{exchange?.name || conn.exchange_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOk ? <CheckCircle size={16} className="text-trading-green" /> : <AlertCircle size={16} className="text-trading-red" />}
          <Badge variant={isOk ? "green" : conn.status === "error" ? "red" : "gray"}>{conn.status}</Badge>
          {conn.is_testnet && <Badge variant="yellow">Testnet</Badge>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {["Read Data", "Spot Trade", "No Withdrawals", "Futures"].map((p, i) => (
          <div key={p} className="flex items-center gap-1.5 text-xs text-gray-400">
            {i < 3 ? <CheckCircle size={11} className="text-trading-green" /> : <XCircle size={11} className="text-gray-600" />}
            {p}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
        <Lock size={11} />
        {conn.last_verified_at ? `Verified ${formatRelative(conn.last_verified_at)}` : "Never verified"}
        {conn.error_message && <span className="text-trading-red ml-1">— {conn.error_message}</span>}
      </div>

      <div className="flex gap-2">
        <button onClick={() => onVerify(conn.id)} disabled={verifying === conn.id} className="btn-secondary flex-1 flex items-center justify-center gap-2 text-xs py-2">
          <RefreshCw size={12} className={verifying === conn.id ? "animate-spin" : ""} />
          {verifying === conn.id ? "Verifying…" : "Verify"}
        </button>
        <button onClick={() => onDelete(conn.id)} className="btn-secondary text-trading-red hover:bg-trading-red/10 py-2 px-3">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  )
}

export default function Exchanges() {
  const [connections, setConnections] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [verifying, setVerifying] = useState(null)

  async function load() {
    try {
      const { data } = await api.get("/exchanges/")
      setConnections(data)
    } catch {} finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  async function verify(id) {
    setVerifying(id)
    try {
      await api.post(`/exchanges/${id}/verify`)
      toast.success("Connection verified!")
      load()
    } catch (err) { toast.error(err.response?.data?.detail || "Verification failed") }
    finally { setVerifying(null) }
  }

  async function remove(id) {
    if (!confirm("Remove this exchange connection?")) return
    try {
      await api.delete(`/exchanges/${id}`)
      setConnections(c => c.filter(x => x.id !== id))
      toast.success("Exchange removed")
    } catch { toast.error("Failed to remove") }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Exchange Connections</h2>
          <p className="text-gray-400 text-sm mt-1">Securely connect your exchange API keys. Withdrawal permissions are always disabled.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Connect Exchange
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Connected", value: connections.length, color: "text-white" },
          { label: "Active", value: connections.filter(c => c.status === "active").length, color: "text-trading-green" },
          { label: "Testnet", value: connections.filter(c => c.is_testnet).length, color: "text-trading-yellow" },
        ].map(s => (
          <div key={s.label} className="card-sm text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Connections grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2].map(i => <div key={i} className="h-48 bg-surface-50 rounded-xl animate-pulse" />)}
        </div>
      ) : connections.length === 0 ? (
        <div>
          <div className="card text-center py-10 mb-6">
            <Plug2 size={40} className="mx-auto text-gray-600 mb-3" />
            <h3 className="font-semibold text-white mb-1">No exchanges connected yet</h3>
            <p className="text-gray-400 text-sm mb-4">Start with a testnet account — no real funds needed</p>
            <button onClick={() => setShowModal(true)} className="btn-primary">Connect Your First Exchange</button>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-3">Supported Exchanges</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {EXCHANGES.map(ex => (
                <div key={ex.id} onClick={() => setShowModal(true)} className="card-sm flex items-center gap-3 cursor-pointer hover:border-surface-300 transition-all">
                  <ExLogo id={ex.id} size={32} />
                  <span className="text-sm font-medium text-white">{ex.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {connections.map(conn => (
              <ConnectionCard key={conn.id} conn={conn} onVerify={verify} onDelete={remove} verifying={verifying} />
            ))}
            {/* Add more card */}
            <button onClick={() => setShowModal(true)} className="card border-dashed border-surface-200 flex flex-col items-center justify-center gap-3 text-gray-500 hover:text-gray-300 hover:border-surface-300 transition-all min-h-[180px]">
              <Plus size={28} />
              <span className="text-sm font-medium">Add Exchange</span>
            </button>
          </div>
          {/* Security note */}
          <div className="p-4 bg-brand-500/5 border border-brand-500/20 rounded-xl flex items-start gap-3 text-sm text-gray-400">
            <Shield size={16} className="text-brand-400 mt-0.5 shrink-0" />
            <p>All API keys are encrypted with AES-256 before storage. Withdrawal permissions are permanently blocked on all connections. Only Read and Trade scopes are used.</p>
          </div>
        </>
      )}
      <ConnectModal isOpen={showModal} onClose={() => setShowModal(false)} onCreated={load} />
    </div>
  )
}
